import React from "react";

import { useModal } from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";

import {
  MAX_INSTRUCTION_PHOTOS,
  type AttachedInstructionPhoto,
  type InstructionPhotoDraft,
} from "~/shared/lib/instruction-photos";

import { InstructionPhotoThumb } from "./instruction-photo-thumb";
import { InstructionPhotosModal } from "./instruction-photos.modal";

export interface InstructionPhotosFieldProps {
  drafts: InstructionPhotoDraft[];
  onDraftsChange: (photos: InstructionPhotoDraft[]) => void;
  attached?: AttachedInstructionPhoto[];
  onRemoveAttached?: (id: bigint) => void;
  disabled?: boolean;
}

export const InstructionPhotosField: React.FC<InstructionPhotosFieldProps> = ({
  drafts,
  onDraftsChange,
  attached = [],
  onRemoveAttached,
  disabled = false,
}) => {
  const t = useT();
  const { openModal, closeModal } = useModal();

  // The cap is per job, so stored photos count toward it alongside the drafts.
  const heldCount = attached.length + drafts.length;
  const isFull = heldCount >= MAX_INSTRUCTION_PHOTOS;

  const handleRemoveDraft = (index: number) => {
    URL.revokeObjectURL(drafts[index].previewUrl);
    onDraftsChange(drafts.filter((_, i) => i !== index));
  };

  const handleOpenUpload = () => {
    const modalId = openModal({
      title: t(Namespace.Orders, "create.uploadPhotoTitle"),
      size: "md",
      bodyClassName: "!p-0",
      closeButtonTop: "top-4",
      body: (
        <InstructionPhotosModal
          heldCount={heldCount}
          onSave={(picked) => {
            onDraftsChange([...drafts, ...picked]);
            closeModal(modalId);
          }}
        />
      ),
    });
  };

  return (
    <div
      className="flex flex-col items-start gap-4 rounded-xl bg-slate-100 p-4"
      data-testid="order-form-photos"
    >
      <p className="flex h-6 items-center text-base font-semibold leading-4 tracking-[-0.01em] text-text-body">
        {t(Namespace.Orders, "create.attachPhotos")}
      </p>

      {heldCount > 0 && (
        <div className="flex flex-wrap gap-2">
          {attached.map((photo) => (
            <InstructionPhotoThumb
              key={String(photo.id)}
              size="card"
              src={photo.url}
              onRemove={
                disabled || !onRemoveAttached ? undefined : () => onRemoveAttached(photo.id)
              }
            />
          ))}
          {drafts.map((photo, index) => (
            <InstructionPhotoThumb
              key={photo.previewUrl}
              size="card"
              src={photo.previewUrl}
              onRemove={disabled ? undefined : () => handleRemoveDraft(index)}
            />
          ))}
        </div>
      )}

      <button
        type="button"
        disabled={disabled || isFull}
        onClick={handleOpenUpload}
        className="flex h-8 items-center rounded-lg px-3 py-2 text-sm font-medium leading-4 tracking-[-0.2px] text-blue-600 hover:text-blue-700 disabled:text-slate-400"
      >
        {t(Namespace.Orders, "create.uploadPhoto")}
      </button>
    </div>
  );
};
